import {
  AndroidLogo,
  AppleLogo,
  Button,
  JavascriptLogo,
  RelativeTime,
  Stack,
  Typography,
  WithTooltip,
} from '@compass/components'
import Skeleton from '@mui/material/Skeleton'
import { AppRoute, buildRoute } from 'appRoutes'
import clsx from 'clsx'
import { LinkButtonLink } from 'components/compass-integration'
import { VisitorIdLink } from 'components/VisitorIdLink'
import { EventFraudLabelsSection } from 'features/fraudAnalysis'
import { useCurrentSubscription, useCurrentSubscriptionData } from 'features/subscription'
import { MIMEType } from 'helpers/types'
import useNormalizedDateTime from 'hooks/useNormalizedDateTime'
import { DateTime } from 'luxon'
import { IdEventV4 } from 'models/event'
import { useMemo } from 'react'

import { useGetEnvironmentName } from '../../hooks/useGetEnvironmentName'
import { CodeViewerModal } from './CodeViewerModal'
import { onQuickFilter } from './onQuickFilter'
import { SidebarDetail } from './SidebarDetail'
import { usePersistedEventFilterParams } from './usePersistedEventFilterParams'

export function SidebarContent({ event, className }: { event?: IdEventV4; className?: string }) {
  const { currentSubscriptionId } = useCurrentSubscription()
  const { subscription } = useCurrentSubscriptionData()
  const highRecallEnabled = subscription?.featureSettings?.highRecallEnabled === true

  return (
    <Stack direction='column' gap={6} className={clsx('overflow-x-hidden p-6 w-full', className)}>
      <Stack direction='column' gap={5}>
        <TimestampDetail timestamp={event?.timestamp} />
        <ProximityIdDetail proximity={event?.proximity} />
        <RequestIdDetail requestId={event?.event_id} />
        <VisitorIdDetail visitorId={event?.identification?.visitor_id} />
        {highRecallEnabled && <HighRecallIdDetail highRecallId={event?.supplementary_id_high_recall?.visitor_id} />}
        {event?.environment_id && <EnvironmentIdDetail environmentId={event.environment_id} />}
        <ClientSdkDetail sdk={event?.sdk} />
        {/* <OriginDetail url={url} /> */}
        <UrlDetail event={event} />
      </Stack>

      <EventFraudLabelsSection
        workspaceId={currentSubscriptionId}
        eventId={event?.event_id}
        eventTimestamp={event?.timestamp}
      />

      <hr className='border-gray-300' />

      <Stack direction='column' gap={5}>
        <Typography size='sm' weight='medium'>
          Custom fields
        </Typography>
        <LinkedIdDetail event={event} />
        <TagDetail event={event} />
      </Stack>
    </Stack>
  )
}

function TimestampDetail({ timestamp }: { timestamp?: IdEventV4['timestamp'] }) {
  const dateTimeObject = useNormalizedDateTime(timestamp)
  const formattedTimestamp = dateTimeObject ? dateTimeObject.toFormat('MM/dd/yyyy HH:mm:ss') : undefined

  return (
    <SidebarDetail>
      {({ Label, ValueRow, Value }) => (
        <>
          <Label>Timestamp</Label>
          <ValueRow copyValue={timestamp ? DateTime.fromMillis(timestamp).toISO()! : undefined} filterNoun='timestamp'>
            {dateTimeObject ? (
              <RelativeTime date={dateTimeObject}>
                <Value className='text-gray-900 font-mono'>{formattedTimestamp}</Value>
              </RelativeTime>
            ) : (
              <Skeleton width={192} height={16} className='mt-1 transform-none' />
            )}
          </ValueRow>
        </>
      )}
    </SidebarDetail>
  )
}

function RequestIdDetail({ requestId }: { requestId?: string }) {
  return (
    <SidebarDetail>
      {({ Label, ValueRow, Value }) => (
        <>
          <Label>Event ID</Label>
          <ValueRow copyValue={requestId} filterNoun='event ID'>
            {requestId ? (
              <Value className='text-gray-900 font-mono'>{requestId}</Value>
            ) : (
              <Skeleton width={168} height={16} className='mt-1 transform-none' />
            )}
          </ValueRow>
        </>
      )}
    </SidebarDetail>
  )
}

function HighRecallIdDetail({ highRecallId }: { highRecallId?: string }) {
  const { currentSubscriptionId: subscriptionId } = useCurrentSubscription()
  const filterParams = usePersistedEventFilterParams()
  const newFilterParams = { ...filterParams, highRecallId }
  const link = buildRoute(AppRoute.IdentificationEvents, { subscriptionId }, newFilterParams)

  return (
    <SidebarDetail>
      {({ Label, ValueRow, Value }) => (
        <>
          <Label>
            <WithTooltip
              content='An identifier optimized for recognizing returning users across sessions and devices.'
              wrapperClassName='cursor-help'
            >
              <span>High Recall ID</span>
            </WithTooltip>
          </Label>
          <ValueRow
            copyValue={highRecallId}
            filterNoun='High Recall ID'
            filterHref={link}
            onFilter={() => onQuickFilter(newFilterParams)}
          >
            <Value className='text-gray-900 font-mono'>{highRecallId}</Value>
          </ValueRow>
        </>
      )}
    </SidebarDetail>
  )
}

export function EnvironmentIdDetail({ environmentId }: { environmentId: string }) {
  const { currentSubscriptionId: subscriptionId } = useCurrentSubscription()
  const filterParams = usePersistedEventFilterParams()
  const newFilterParams = { ...filterParams, environment: environmentId }
  const link = buildRoute(AppRoute.IdentificationEvents, { subscriptionId }, newFilterParams)
  const environmentName = useGetEnvironmentName(environmentId)

  return (
    <SidebarDetail>
      {({ Label, ValueRow, Value }) => (
        <>
          <Label>
            <span>Environment</span>
          </Label>
          <ValueRow filterNoun='environment' filterHref={link} onFilter={() => onQuickFilter(newFilterParams)}>
            {environmentName === null ? (
              <Skeleton width={120} height={16} className='mt-1 transform-none' />
            ) : (
              <Value className={clsx('text-gray-900', !environmentName && 'font-mono')}>
                {environmentName ?? environmentId}
              </Value>
            )}
          </ValueRow>
        </>
      )}
    </SidebarDetail>
  )
}

function VisitorIdDetail({ visitorId }: { visitorId?: string }) {
  const { currentSubscriptionId: subscriptionId } = useCurrentSubscription()
  const filterParams = usePersistedEventFilterParams()
  const newFilterParams = { ...filterParams, visitorId: visitorId ?? '' }
  const link = buildRoute(AppRoute.IdentificationEvents, { subscriptionId }, newFilterParams)

  return (
    <SidebarDetail>
      {({ Label, ValueRow, Value }) => (
        <>
          <Label>Visitor ID</Label>
          <ValueRow
            copyValue={visitorId}
            filterNoun='visitor ID'
            filterHref={link}
            onFilter={() => onQuickFilter(newFilterParams)}
          >
            {visitorId ? (
              <VisitorIdLink visitorId={visitorId} workspaceId={subscriptionId} showCopy={false}>
                {({ canNavigate, linkProps }) =>
                  canNavigate ? (
                    <LinkButtonLink
                      {...linkProps}
                      variant='ghost'
                      className='text-gray-900 font-mono font-normal -mx-3 -my-2'
                    >
                      {visitorId}
                    </LinkButtonLink>
                  ) : (
                    <Value className='text-gray-900 font-mono'>{visitorId}</Value>
                  )
                }
              </VisitorIdLink>
            ) : (
              <Skeleton width={168} height={16} className='mt-1 transform-none' />
            )}
          </ValueRow>
        </>
      )}
    </SidebarDetail>
  )
}

function ProximityIdDetail({ proximity }: { proximity?: IdEventV4['proximity'] }) {
  const { currentSubscriptionId: subscriptionId } = useCurrentSubscription()
  const filterParams = usePersistedEventFilterParams()
  const newFilterParams = { ...filterParams, proximityId: proximity?.id ?? '' }
  const link = buildRoute(AppRoute.IdentificationEvents, { subscriptionId }, newFilterParams)

  if (!proximity?.id) {
    return null
  }

  return (
    <SidebarDetail>
      {({ Label, ValueRow, Value }) => (
        <>
          <Label>Proximity ID</Label>
          <ValueRow
            copyValue={proximity.id}
            filterNoun='proximity ID'
            filterHref={link}
            onFilter={() => onQuickFilter(newFilterParams)}
          >
            <Value className='text-gray-900 font-mono'>{proximity.id}</Value>
          </ValueRow>
        </>
      )}
    </SidebarDetail>
  )
}

const platformMapper = {
  js: { label: 'JavaScript', icon: <JavascriptLogo size={16} /> },
  ios: { label: 'iOS', icon: <AppleLogo size={16} /> },
  android: { label: 'Android', icon: <AndroidLogo size={16} /> },
  unknown: { label: 'Unknown', icon: null },
}

function ClientSdkDetail({ sdk }: { sdk?: IdEventV4['sdk'] }) {
  const { currentSubscriptionId: subscriptionId } = useCurrentSubscription()
  const filterParams = usePersistedEventFilterParams()
  const newFilterParams = {
    ...filterParams,
    sdkPlatform: sdk?.platform ?? '',
    sdkVersion: sdk?.version ?? '',
  }
  const link = buildRoute(AppRoute.IdentificationEvents, { subscriptionId }, newFilterParams)

  if (!sdk?.platform || sdk?.platform === 'unknown') {
    return null
  }

  const versionLabel = sdk?.version ? ` v${sdk.version}` : ''
  const platform = platformMapper[sdk?.platform]

  return (
    <SidebarDetail>
      {({ Label, ValueRow, Value }) => (
        <>
          <Label>Client SDK</Label>
          <ValueRow
            copyValue={platform.label + versionLabel}
            filterNoun='client SDK'
            filterHref={link}
            onFilter={() => onQuickFilter(newFilterParams)}
          >
            {sdk ? (
              <Value className='flex items-center gap-1'>
                {platform.icon}
                {platform.label}
                {versionLabel}
              </Value>
            ) : (
              <Skeleton width={96} height={16} className='mt-1 transform-none' />
            )}
          </ValueRow>
        </>
      )}
    </SidebarDetail>
  )
}

const TRUNCATION_LENGTH = 20
function UrlDetail({ event }: { event?: IdEventV4 }) {
  const { currentSubscriptionId: subscriptionId } = useCurrentSubscription()
  const filterParams = usePersistedEventFilterParams()

  const url = event?.url
  const isMobile = event?.sdk?.platform === 'ios' || event?.sdk?.platform === 'android'
  const appIdentifier = event?.bundle_id || event?.package_name

  const filterValue = url || appIdentifier || ''
  const newFilterParams = { ...filterParams, url: filterValue }
  const link = buildRoute(AppRoute.IdentificationEvents, { subscriptionId }, newFilterParams)

  let urlObject: URL | undefined
  try {
    if (url) {
      urlObject = new URL(url)
    }
  } catch {
    /* The URL is not an HTTP URL, but a package bundle. */
  }

  return (
    <SidebarDetail>
      {({ Label, ValueRow, Value }) => (
        <>
          <Label>{isMobile ? 'App identifier' : 'URL'}</Label>
          <ValueRow
            copyValue={url || appIdentifier}
            filterNoun={isMobile ? 'app identifier' : 'URL'}
            filterHref={filterValue ? link : undefined}
            onFilter={filterValue ? () => onQuickFilter(newFilterParams) : undefined}
          >
            {url ? (
              <>
                {/* Parsed URL with parts. */}
                {urlObject ? (
                  <WithTooltip content={url} focusable={false} className='break-all' wrapperClassName='cursor-text'>
                    <span className='flex flex-col'>
                      {urlObject.pathname ? (
                        <Value>
                          {urlObject.pathname?.length > TRUNCATION_LENGTH ? (
                            <>
                              <span className='text-gray-800 font-mono'>/&hellip;</span>
                              {urlObject.pathname.slice(1).slice(-1 * TRUNCATION_LENGTH)}
                            </>
                          ) : (
                            urlObject.pathname
                          )}
                        </Value>
                      ) : null}
                      {urlObject.search ? (
                        <Value>
                          {urlObject.search?.length > TRUNCATION_LENGTH ? (
                            <>
                              <span className='text-gray-800 font-mono'>?&hellip;</span>
                              {urlObject.search.slice(1).slice(-1 * TRUNCATION_LENGTH)}
                            </>
                          ) : (
                            urlObject.search
                          )}
                        </Value>
                      ) : null}
                      {urlObject.hash ? (
                        <Value>
                          {urlObject.hash?.length > TRUNCATION_LENGTH ? (
                            <>
                              <span className='text-gray-800 font-mono'>#&hellip;</span>
                              {urlObject.hash.slice(1).slice(-1 * TRUNCATION_LENGTH)}
                            </>
                          ) : (
                            urlObject.hash
                          )}
                        </Value>
                      ) : null}
                    </span>
                  </WithTooltip>
                ) : null}

                {/* Fallback to full URL if parsing failed. */}
                {urlObject == null && url ? (
                  <WithTooltip content={url} focusable={false} wrapperClassName='cursor-text'>
                    <Value>
                      {url.length > TRUNCATION_LENGTH ? (
                        <>
                          <span className='text-gray-800 font-mono'>&hellip;</span>
                          {url.slice(-1 * TRUNCATION_LENGTH)}
                        </>
                      ) : (
                        url
                      )}
                    </Value>
                  </WithTooltip>
                ) : null}
              </>
            ) : isMobile && appIdentifier ? (
              <WithTooltip content={appIdentifier} focusable={false} wrapperClassName='cursor-text'>
                <Value>
                  {appIdentifier.length > TRUNCATION_LENGTH ? (
                    <>
                      <span className='text-gray-800 font-mono'>&hellip;</span>
                      {appIdentifier.slice(-1 * TRUNCATION_LENGTH)}
                    </>
                  ) : (
                    appIdentifier
                  )}
                </Value>
              </WithTooltip>
            ) : !event ? (
              <Stack direction='column' gap={1.5}>
                <Skeleton width={192} height={16} className='transform-none!' />
                <Skeleton width='100%' height={16} className='transform-none!' />
              </Stack>
            ) : (
              <Value className='text-gray-600'>-</Value>
            )}
          </ValueRow>
        </>
      )}
    </SidebarDetail>
  )
}

function LinkedIdDetail({ event }: { event?: IdEventV4 }) {
  const { currentSubscriptionId: subscriptionId } = useCurrentSubscription()
  const filterParams = usePersistedEventFilterParams()
  const newFilterParams = { ...filterParams, linkedId: event?.linked_id ?? '' }
  const link = buildRoute(AppRoute.IdentificationEvents, { subscriptionId }, newFilterParams)

  return (
    <SidebarDetail>
      {({ Label, ValueRow, Value }) => (
        <>
          <Label>Linked ID</Label>
          <ValueRow
            copyValue={event?.linked_id}
            filterNoun='linked ID'
            {...(event?.linked_id && {
              filterHref: link,
              onFilter: () => onQuickFilter(newFilterParams),
            })}
          >
            {event ? (
              <Value className={!event?.linked_id ? 'text-gray-600' : 'text-gray-900'}>
                {event?.linked_id ?? 'Not provided'}
              </Value>
            ) : (
              <Skeleton width={88} height={16} className='mt-1 transform-none' />
            )}
          </ValueRow>
        </>
      )}
    </SidebarDetail>
  )
}

const TAG_MAX_LENGTH = 20
function TagDetail({ event }: { event?: IdEventV4 }) {
  const tags = event?.tags
  const stringified = useMemo(() => JSON.stringify(tags, null, 2), [tags])
  const truncateTag = (stringified?.length ?? 0) > TAG_MAX_LENGTH
  const displayedTag = truncateTag ? <>{stringified?.slice(0, TAG_MAX_LENGTH)}&hellip;</> : stringified || '-'

  const tagFieldCount = typeof tags === 'object' && tags !== null ? Object.keys(tags).length : 0

  return (
    <SidebarDetail>
      {({ Label, ValueRow, Value }) => (
        <>
          <Label>Tag</Label>
          <ValueRow>
            {event ? (
              <Stack direction='column' gap={2} className='items-start'>
                <Value className='text-gray-900 font-mono'>{displayedTag}</Value>
                {truncateTag ? (
                  <CodeViewerModal fileName='tag.json' mimeType={MIMEType.PlainText} value={stringified}>
                    <Button variant='outline' size='xs' alt>
                      Show {tagFieldCount === 1 ? '1 hidden field' : `${tagFieldCount} hidden fields`}
                    </Button>
                  </CodeViewerModal>
                ) : null}
              </Stack>
            ) : (
              <Stack direction='column' gap={1}>
                <Skeleton width={192} height={20} className='transform-none!' />
                <Skeleton width={80} height={20} className='transform-none!' />
              </Stack>
            )}
          </ValueRow>
        </>
      )}
    </SidebarDetail>
  )
}
